import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';

const TransactionTable = ({ onTransactionSelect, filters }) => {
  const [sortField, setSortField] = useState('timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Mock transaction data
  const transactions = [
    {
      id: 'tx1',
      hash: '0x1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b',
      fromAddress: '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
      toAddress: '1BvBMSEYstWetqTFn5Au4m4GFg7xJaNVN2',
      amount: 25.5,
      fee: 0.0001,
      timestamp: new Date('2024-09-14T10:30:00Z'),
      confirmations: 156,
      blockHeight: 850123,
      riskScore: 85,
      status: 'confirmed',
      flags: ['suspicious_amount', 'new_address']
    },
    {
      id: 'tx2',
      hash: '0x2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c',
      fromAddress: '1BvBMSEYstWetqTFn5Au4m4GFg7xJaNVN2',
      toAddress: '3J98t1WpEZ73CNmQviecrnyiWrnqRhWNLy',
      amount: 15.2,
      fee: 0.0002,
      timestamp: new Date('2024-09-14T11:45:00Z'),
      confirmations: 142,
      blockHeight: 850137,
      riskScore: 20,
      status: 'confirmed',
      flags: ['exchange_deposit']
    },
    {
      id: 'tx3',
      hash: '0x3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d',
      fromAddress: '1BvBMSEYstWetqTFn5Au4m4GFg7xJaNVN2',
      toAddress: '1F1tAaz5x1HUXrCNLbtMDqcw6o5GNn4xqX',
      amount: 8.7,
      fee: 0.0003,
      timestamp: new Date('2024-09-14T12:15:00Z'),
      confirmations: 138,
      blockHeight: 850141,
      riskScore: 95,
      status: 'confirmed',
      flags: ['mixer_service', 'high_risk']
    },
    {
      id: 'tx4',
      hash: '0x4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e',
      fromAddress: '1F1tAaz5x1HUXrCNLbtMDqcw6o5GNn4xqX',
      toAddress: '1Lbcfr7sAHTD9CgdQo3HTMTkV8LK4ZnX71',
      amount: 4.3,
      fee: 0.0002,
      timestamp: new Date('2024-09-14T13:20:00Z'),
      confirmations: 125,
      blockHeight: 850154,
      riskScore: 60,
      status: 'confirmed',
      flags: ['fund_splitting']
    },
    {
      id: 'tx5',
      hash: '0x5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f',
      fromAddress: '1F1tAaz5x1HUXrCNLbtMDqcw6o5GNn4xqX',
      toAddress: '1MK2SgwjheyYKuYdgVjjyJFTvJ5wpGmxaj',
      amount: 4.4,
      fee: 0.0002,
      timestamp: new Date('2024-09-14T13:25:00Z'),
      confirmations: 124,
      blockHeight: 850155,
      riskScore: 60,
      status: 'confirmed',
      flags: ['fund_splitting']
    },
    {
      id: 'tx6',
      hash: '0x6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a',
      fromAddress: '1Lbcfr7sAHTD9CgdQo3HTMTkV8LK4ZnX71',
      toAddress: '1N4Qbzg6LSXUXyXu2MDuGfzxwMA7do8AyL',
      amount: 4.25,
      fee: 0.0001,
      timestamp: new Date('2024-09-14T14:10:00Z'),
      confirmations: 118,
      blockHeight: 850161,
      riskScore: 30,
      status: 'confirmed',
      flags: ['normal']
    }
  ];

  const filteredAndSortedTransactions = useMemo(() => {
    let filtered = transactions?.filter(tx => {
      const matchesSearch = searchTerm === '' || 
        tx?.hash?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        tx?.fromAddress?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        tx?.toAddress?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      
      const matchesAmount = !filters?.minAmount || tx?.amount >= filters?.minAmount;
      const matchesRisk = !filters?.riskLevel || tx?.riskScore >= filters?.riskLevel;
      
      return matchesSearch && matchesAmount && matchesRisk;
    });

    return filtered?.sort((a, b) => {
      let aVal = a?.[sortField];
      let bVal = b?.[sortField];
      
      if (sortField === 'timestamp') {
        aVal = aVal?.getTime();
        bVal = bVal?.getTime();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  }, [transactions, searchTerm, sortField, sortDirection, filters]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleTransactionClick = (transaction) => {
    setSelectedTransaction(transaction);
    onTransactionSelect && onTransactionSelect(transaction);
  };

  const getRiskBadgeColor = (riskScore) => {
    if (riskScore >= 80) return 'bg-error text-error-foreground';
    if (riskScore >= 50) return 'bg-warning text-warning-foreground';
    return 'bg-success text-success-foreground';
  };

  const formatAddress = (address) => {
    return `${address?.slice(0, 8)}...${address?.slice(-6)}`;
  };

  const formatTimestamp = (timestamp) => {
    return timestamp?.toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <Icon name="ArrowUpDown" size={14} color="var(--color-text-secondary)" />;
    }
    return (
      <Icon 
        name={sortDirection === 'asc' ? "ArrowUp" : "ArrowDown"} 
        size={14} 
        color="var(--color-primary)" 
      />
    );
  };

  return (
    <div className="bg-surface border border-border rounded-lg shadow-forensic-card h-full flex flex-col">
      {/* Table Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="List" size={20} color="var(--color-primary)" />
            <h3 className="text-lg font-semibold text-text-primary">Transaction Details</h3>
            <span className="text-sm text-text-secondary bg-muted px-2 py-1 rounded">
              {filteredAndSortedTransactions?.length} transactions
            </span>
          </div>
        </div>
        
        {/* Search */}
        <Input
          type="search"
          placeholder="Search by hash or address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e?.target?.value)}
          className="max-w-md"
        />
      </div>
      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full">
          <thead className="bg-muted sticky top-0">
            <tr>
              <th className="text-left p-3 text-sm font-semibold text-text-primary">
                <button
                  onClick={() => handleSort('timestamp')}
                  className="flex items-center space-x-1 hover:text-primary transition-forensic"
                >
                  <span>Time</span>
                  <SortIcon field="timestamp" />
                </button>
              </th>
              <th className="text-left p-3 text-sm font-semibold text-text-primary">
                Transaction Hash
              </th>
              <th className="text-left p-3 text-sm font-semibold text-text-primary">
                From → To
              </th>
              <th className="text-right p-3 text-sm font-semibold text-text-primary">
                <button
                  onClick={() => handleSort('amount')}
                  className="flex items-center space-x-1 hover:text-primary transition-forensic ml-auto"
                >
                  <span>Amount</span>
                  <SortIcon field="amount" />
                </button>
              </th>
              <th className="text-center p-3 text-sm font-semibold text-text-primary">
                <button
                  onClick={() => handleSort('riskScore')}
                  className="flex items-center space-x-1 hover:text-primary transition-forensic mx-auto"
                >
                  <span>Risk</span>
                  <SortIcon field="riskScore" />
                </button>
              </th>
              <th className="text-center p-3 text-sm font-semibold text-text-primary">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedTransactions?.map((tx) => (
              <tr
                key={tx?.id}
                onClick={() => handleTransactionClick(tx)}
                className={`
                  border-b border-border cursor-pointer transition-forensic hover:bg-muted
                  ${selectedTransaction?.id === tx?.id ? 'bg-accent/10' : ''}
                `}
              >
                <td className="p-3">
                  <div className="text-sm text-text-primary font-medium">
                    {formatTimestamp(tx?.timestamp)}
                  </div>
                  <div className="text-xs text-text-secondary">
                    Block {tx?.blockHeight}
                  </div>
                </td>
                <td className="p-3">
                  <div className="text-sm font-data text-text-primary">
                    {formatAddress(tx?.hash)}
                  </div>
                  <div className="text-xs text-text-secondary">
                    {tx?.confirmations} confirmations
                  </div>
                </td>
                <td className="p-3">
                  <div className="space-y-1">
                    <div className="text-xs text-text-secondary">From:</div>
                    <div className="text-sm font-data text-text-primary">
                      {formatAddress(tx?.fromAddress)}
                    </div>
                    <div className="text-xs text-text-secondary">To:</div>
                    <div className="text-sm font-data text-text-primary">
                      {formatAddress(tx?.toAddress)}
                    </div>
                  </div>
                </td>
                <td className="p-3 text-right">
                  <div className="text-sm font-semibold text-text-primary">
                    {tx?.amount?.toFixed(8)} BTC
                  </div>
                  <div className="text-xs text-text-secondary">
                    Fee: {tx?.fee?.toFixed(8)}
                  </div>
                </td>
                <td className="p-3 text-center">
                  <span className={`
                    inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                    ${getRiskBadgeColor(tx?.riskScore)}
                  `}>
                    {tx?.riskScore}
                  </span>
                </td>
                <td className="p-3 text-center">
                  <div className="flex flex-col items-center space-y-1">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success text-success-foreground">
                      {tx?.status}
                    </span>
                    {tx?.flags?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {tx?.flags?.slice(0, 2)?.map((flag) => (
                          <span
                            key={flag}
                            className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-warning/20 text-warning"
                          >
                            {flag?.replace('_', ' ')}
                          </span>
                        ))}
                        {tx?.flags?.length > 2 && (
                          <span className="text-xs text-text-secondary">
                            +{tx?.flags?.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Table Footer */}
      <div className="p-4 border-t border-border bg-muted">
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <div>
            Showing {filteredAndSortedTransactions?.length} of {transactions?.length} transactions
          </div>
          <div className="flex items-center space-x-4">
            <div>
              Total Volume: {filteredAndSortedTransactions?.reduce((sum, tx) => sum + tx?.amount, 0)?.toFixed(8)} BTC
            </div>
            <div>
              Avg Risk: {Math.round(filteredAndSortedTransactions?.reduce((sum, tx) => sum + tx?.riskScore, 0) / filteredAndSortedTransactions?.length || 0)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionTable;